/* eslint-disable array-bracket-newline */
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IDexTrade, enums } from '@kattana-io/common';
import TradesTable from './Table';
import TradesTableMobile from './TableMobile';
import { IStore } from '../../../reducers';
import { actionSetTradesWallet } from '../../../actions/dex_trades';
import { authTokenSelector } from '../../../selectors/auth';
import { actionShowModal } from '../../../actions/modal';
import { ModalTypes } from '../../../reducers/modal';
import { useGetTrades } from './useGetTrades';

export interface ITradesDataLayerProps {
  width?: number;
  isMobile?: boolean;
  currentWallet?: string;
  openWalletModal?(props: any): void;
}

const TradesDataLayer = ({
  width,
  isMobile,
  currentWallet,
  openWalletModal,
}: ITradesDataLayerProps) => {
  const ticker = useSelector((state: IStore) => state.current);
  const tradesWallet = useSelector(
    (state: IStore) => state.dex_trades.tradesWallet
  );
  const drawMarks = useSelector((state: IStore) => state.dex_trades.drawMarks);
  const removeMarks = useSelector(
    (state: IStore) => state.dex_trades.removeMarks
  );

  const plan = useSelector((state: IStore) => state.features.currentPlan);
  const timezone = useSelector((state: IStore) => state.timezone.current);
  const authToken = useSelector(authTokenSelector);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { trades, loading, nextPageCanBeLoaded, onScroll } = useGetTrades(
    ticker,
    plan,
    authToken
  );

  const date = useMemo(() => {
    return trades[trades.length - 1]?.timestamp;
  }, [trades]);

  // Set trades wallet to the clicked one and show marks on chart
  const onTradeClick = useCallback(
    ({ wallet }: IDexTrade) => {
      if (wallet !== tradesWallet) {
        dispatch(actionSetTradesWallet(wallet));
        removeMarks?.();
        drawMarks?.();
      } else {
        dispatch(actionSetTradesWallet(null));
        removeMarks?.();
      }
    },
    [dispatch, drawMarks, removeMarks, tradesWallet]
  );

  const popupData = useMemo(() => {
    if (plan === enums.PricingPlan.premium || nextPageCanBeLoaded) {
      return undefined;
    }
    return {
      text: 'Unlock Premium Access and Enjoy Unlimited Content',
      buttonText: 'Get Premium',
      onSubmit: () =>
        authToken
          ? navigate('/subscription')
          : dispatch(actionShowModal(ModalTypes.connect, {})),
    };
  }, [plan, navigate, authToken, nextPageCanBeLoaded, dispatch]);

  return isMobile ? (
    <TradesTableMobile
      date={date}
      timezone={timezone}
      trades={trades}
      base={ticker.base}
      quote={ticker.quote}
      flipped={ticker.flipped}
      address={ticker.address}
      chain={ticker.chain}
      loading={loading}
    />
  ) : (
    <TradesTable
      date={date}
      timezone={timezone}
      currentWallet={tradesWallet || currentWallet}
      trades={trades}
      base={ticker.base}
      quote={ticker.quote}
      loading={loading}
      flipped={ticker.flipped}
      address={ticker.address}
      width={width}
      chain={ticker.chain}
      plan={plan}
      tablePopup={popupData}
      onTradeClick={onTradeClick}
      openWalletModal={openWalletModal!}
      onScroll={onScroll}
    />
  );
};

export default TradesDataLayer;
